/**
 * Keyboard modality: `<html data-keyboard>` while the player is using a
 * keyboard, cleared by any pointer press (mouse, touch or pen). The board's
 * typing cursor (the ring and direction arrow in Cell.css) is drawn only
 * then: after a tap it was a stray highlight, and on a phone, with no keyboard
 * to type on, it sat on a square for the whole game.
 *
 * Why not `:focus-visible`: the board takes focus programmatically (game
 * start, dialogs closing). With no earlier input, as in a reloaded or restored
 * tab, Chromium and WebKit match `:focus-visible` on that focus, so a phone
 * showed the cursor until the first tap. The last input event is exact.
 */

/** A text field: typing there is text entry, not keyboard navigation. */
export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true;
}

/** Track the input modality on `root` (default `<html>`). Returns an uninstaller. */
export function installInputModality(root: HTMLElement = document.documentElement): () => void {
  const onKeyDown = (e: KeyboardEvent) => {
    // Browser and OS shortcuts (Cmd+Tab, Ctrl+L) do not move focus around the
    // page. Typing in a field is not navigation either: that includes a phone's
    // on-screen keyboard, whose Go key submits the join form. Tab out of a
    // field is navigation.
    if (e.metaKey || e.ctrlKey || (isEditable(e.target) && e.key !== 'Tab')) return;
    root.dataset.keyboard = '';
  };
  const onPointerDown = () => {
    delete root.dataset.keyboard;
  };
  // Capture phase: the flag is set before any handler moves focus on this key.
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('pointerdown', onPointerDown, true);
  return () => {
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('pointerdown', onPointerDown, true);
    delete root.dataset.keyboard;
  };
}
